/**
 * Admin → Payments
 *
 * Clean, professional re-implementation. Matches the rest of the admin
 * console style (AdminBookingsPage, AdminBillingPage, AdminProviderPayoutsPage):
 *   - AdminLayout wrapper (provides sidebar + top header consistent across
 *     admin pages).
 *   - Header / metric cards / filter bar / data table / detail dialog.
 *   - Tailwind utilities only, no bespoke CSS.
 *   - 100% DB-driven via /admin/payments and /admin/payments-stats.
 *   - Amounts shown are GROSS (incl-VAT) — matches the customer's quote.
 *     The detail dialog back-derives base + VAT for transparency.
 */

import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CreditCard,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
  Eye,
} from 'lucide-react';

type PaymentStatus =
  | 'succeeded'
  | 'completed'
  | 'pending'
  | 'failed'
  | 'refunded'
  | string;

interface Payment {
  id: number;
  quote_id: number | null;
  booking_id: number | null;
  amount: number;
  amount_pence: number;
  currency: string;
  payment_method: string;
  payment_status: PaymentStatus;
  transaction_id: string;
  paid_at: string | null;
  created_at: string;
  customer_name: string;
  customer_email: string;
  quote_amount_pence: number | null;
  quote_status: string;
  service_name: string;
  property_type: string;
}

interface PaymentStats {
  totalPayments: number;
  totalAmount: number;
  completedAmount: number;
  pendingAmount: number;
  failedAmount: number;
}

const VAT_RATE = 0.2;

const fmtGBP = (n: number) =>
  '£' +
  (Number(n) || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; icon: typeof CheckCircle2 }
> = {
  succeeded: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
  completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
  pending:   { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
  failed:    { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
  refunded:  { bg: 'bg-blue-100', text: 'text-blue-800', icon: RefreshCw },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.pending;
  const Icon = cfg.icon;
  return (
    <Badge
      className={`${cfg.bg} ${cfg.text} border-0 inline-flex items-center gap-1 capitalize`}
    >
      <Icon className="w-3 h-3" />
      {status || 'unknown'}
    </Badge>
  );
};

const MetricCard = ({
  label,
  value,
  sub,
  accent,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  accent: 'teal' | 'green' | 'amber' | 'red';
  icon: typeof DollarSign;
}) => {
  const border: Record<string, string> = {
    teal: 'border-l-teal-500',
    green: 'border-l-green-500',
    amber: 'border-l-amber-500',
    red: 'border-l-red-500',
  };
  const iconColor: Record<string, string> = {
    teal: 'text-teal-600',
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  };
  return (
    <Card className={`border-l-4 ${border[accent]} bg-white`}>
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
            {label}
          </p>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <Icon className={`w-8 h-8 ${iconColor[accent]} opacity-80`} />
      </CardContent>
    </Card>
  );
};

const AdminPaymentsPage = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const [methodFilter, setMethodFilter] = useState<'all' | string>('all');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        apiService.request('/admin/payments', 'GET'),
        apiService.request('/admin/payments-stats', 'GET'),
      ]);
      if (paymentsRes?.success && Array.isArray(paymentsRes.data)) {
        setPayments(paymentsRes.data);
      } else {
        setPayments([]);
      }
      if (statsRes?.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter((p) => {
      if (
        statusFilter !== 'all' &&
        p.payment_status?.toLowerCase() !== statusFilter
      )
        return false;
      if (
        methodFilter !== 'all' &&
        p.payment_method?.toLowerCase() !== methodFilter.toLowerCase()
      )
        return false;
      if (q) {
        const hay = [
          p.customer_name,
          p.customer_email,
          p.transaction_id,
          String(p.id),
          String(p.quote_id || ''),
          String(p.booking_id || ''),
          p.service_name,
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [payments, search, statusFilter, methodFilter]);

  const uniqueMethods = useMemo(
    () =>
      Array.from(
        new Set(payments.map((p) => p.payment_method).filter(Boolean))
      ),
    [payments]
  );

  const filteredTotal = useMemo(
    () => filtered.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [filtered]
  );

  return (
    <>
      <Helmet>
        <title>Payments — Virewire Admin</title>
      </Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          <div className="max-w-7xl mx-auto px-6 py-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
                <p className="text-sm text-gray-600 mt-1">
                  All customer payments, refunds and pending transactions
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={loadData}
                  disabled={loading}
                  className="text-sm"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard
                label="Total Payments"
                value={String(stats?.totalPayments ?? payments.length)}
                sub="All transactions"
                accent="teal"
                icon={CreditCard}
              />
              <MetricCard
                label="Total Revenue"
                value={fmtGBP(stats?.totalAmount ?? 0)}
                sub="Gross incl-VAT"
                accent="green"
                icon={DollarSign}
              />
              <MetricCard
                label="Pending"
                value={fmtGBP(stats?.pendingAmount ?? 0)}
                sub="Awaiting capture"
                accent="amber"
                icon={Clock}
              />
              <MetricCard
                label="Failed"
                value={fmtGBP(stats?.failedAmount ?? 0)}
                sub="Need review"
                accent="red"
                icon={AlertCircle}
              />
            </div>

            {/* Error banner */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by customer, email, transaction ID, service…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    <option value="all">All statuses</option>
                    <option value="succeeded">Succeeded</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                  <select
                    value={methodFilter}
                    onChange={(e) => setMethodFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    <option value="all">All methods</option>
                    {uniqueMethods.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {filtered.length} payment{filtered.length !== 1 ? 's' : ''}{' '}
                    shown
                  </span>
                  <span>
                    Filtered total:{' '}
                    <strong className="text-gray-900">
                      {fmtGBP(filteredTotal)}
                    </strong>
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Payments Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-12 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-gray-400" />
                    Loading payments…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">
                      No payments match the current filters
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Date
                          </th>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Transaction
                          </th>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Customer
                          </th>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Service
                          </th>
                          <th className="text-right text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Amount
                          </th>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Method
                          </th>
                          <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Status
                          </th>
                          <th className="text-right text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filtered.map((p) => (
                          <tr
                            key={p.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {fmtDate(p.paid_at || p.created_at)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">
                              {p.transaction_id || `#${p.id}`}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">
                                {p.customer_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {p.customer_email}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {p.service_name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 whitespace-nowrap">
                              {fmtGBP(p.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 capitalize whitespace-nowrap">
                              {p.payment_method || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <StatusBadge status={p.payment_status} />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedPayment(p)}
                                className="text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Payment Detail Dialog */}
        <Dialog
          open={!!selectedPayment}
          onOpenChange={(open) => !open && setSelectedPayment(null)}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-teal-600" />
                Payment Details
              </DialogTitle>
            </DialogHeader>
            {selectedPayment &&
              (() => {
                const p = selectedPayment;
                // Stored amount is GROSS (incl-VAT). Back-derive base + VAT
                // for the breakdown so the customer-facing math is visible.
                const grossPence =
                  p.amount_pence || Math.round((p.amount || 0) * 100);
                const basePence = Math.round(grossPence / (1 + VAT_RATE));
                const vatPence = grossPence - basePence;
                return (
                  <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          Transaction ID
                        </p>
                        <p className="font-mono text-gray-900 mt-0.5 break-all">
                          {p.transaction_id || `#${p.id}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          Status
                        </p>
                        <div className="mt-1">
                          <StatusBadge status={p.payment_status} />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Customer
                      </p>
                      <p className="font-medium text-gray-900">
                        {p.customer_name}
                      </p>
                      <p className="text-gray-600 text-xs">
                        {p.customer_email}
                      </p>
                    </div>

                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Service
                      </p>
                      <p className="text-gray-900">{p.service_name || '—'}</p>
                      {p.property_type && p.property_type !== 'N/A' && (
                        <p className="text-gray-600 text-xs">
                          {p.property_type}
                        </p>
                      )}
                    </div>

                    <div className="border-t pt-3 bg-gray-50 -mx-6 px-6 py-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Amount Breakdown
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal (ex-VAT)</span>
                          <span className="text-gray-900">
                            {fmtGBP(basePence / 100)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            VAT ({(VAT_RATE * 100).toFixed(0)}%)
                          </span>
                          <span className="text-gray-900">
                            {fmtGBP(vatPence / 100)}
                          </span>
                        </div>
                        <div className="flex justify-between text-base font-bold border-t pt-2 mt-1">
                          <span className="text-gray-900">Total Paid</span>
                          <span className="text-teal-700">
                            {fmtGBP(grossPence / 100)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-3 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="font-semibold text-gray-500 uppercase">
                          Method
                        </p>
                        <p className="text-gray-900 capitalize mt-0.5">
                          {p.payment_method || '—'}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-500 uppercase">
                          Paid
                        </p>
                        <p className="text-gray-900 mt-0.5">
                          {fmtDate(p.paid_at)}
                        </p>
                      </div>
                      {p.booking_id && (
                        <div>
                          <p className="font-semibold text-gray-500 uppercase">
                            Booking
                          </p>
                          <p className="text-gray-900 mt-0.5">
                            #{p.booking_id}
                          </p>
                        </div>
                      )}
                      {p.quote_id && (
                        <div>
                          <p className="font-semibold text-gray-500 uppercase">
                            Quote
                          </p>
                          <p className="text-gray-900 mt-0.5">#{p.quote_id}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </>
  );
};

export default AdminPaymentsPage;
